import type { CheckpointRolloutRow } from "./checkpoint-rollout";

/** Human-readable inventory for active tokens without exposing raw secrets. */
export function formatActiveMaterialInventory(
  row: Pick<CheckpointRolloutRow, "activeQrTokens" | "activeNfcTokens" | "activeCheckpointMaterial" | "hasCheckpointMaterial">,
): string {
  if (!row.hasCheckpointMaterial || row.activeCheckpointMaterial <= 0) {
    return "No active material";
  }

  const parts: string[] = [];
  if (row.activeQrTokens > 0) {
    parts.push(`${row.activeQrTokens} QR`);
  }
  if (row.activeNfcTokens > 0) {
    parts.push(`${row.activeNfcTokens} NFC`);
  }

  if (parts.length === 0) {
    return `${row.activeCheckpointMaterial} active token${row.activeCheckpointMaterial === 1 ? "" : "s"}`;
  }

  return parts.join(" · ");
}

export function hasDuplicateActiveMaterial(
  row: Pick<CheckpointRolloutRow, "activeCheckpointMaterial">,
): boolean {
  return row.activeCheckpointMaterial > 1;
}

/**
 * PRD-001I Wave A2 material state for a pin with no route. Three states, and "unknown" is a
 * first-class one: management_checkpoint_material_status may not be applied yet on a remote
 * the console already deploys against, and guessing "none" there is what told operators to
 * mint again as if their field mint never happened.
 */
export type UnroutedMaterialState = "unknown" | "none" | "present";

export interface UnroutedMaterialCounts {
  activeQrTokens: number;
  activeNfcTokens: number;
  activeCheckpointMaterial: number;
}

export function unroutedMaterialState(
  counts: UnroutedMaterialCounts | undefined,
): UnroutedMaterialState {
  if (counts === undefined) return "unknown";
  return counts.activeCheckpointMaterial > 0 ? "present" : "none";
}

/**
 * Counts and types only (Q4a). Never a token body, never a human code - those are shown once
 * at mint in the session kit and never again, which is exactly what this badge replaces the
 * need for.
 */
export function formatUnroutedMaterialBadge(counts: UnroutedMaterialCounts): string {
  const parts: string[] = [];
  if (counts.activeQrTokens > 0) {
    parts.push(counts.activeQrTokens === 1 ? "QR on file" : `${counts.activeQrTokens} QR on file`);
  }
  if (counts.activeNfcTokens > 0) {
    parts.push(
      counts.activeNfcTokens === 1 ? "NFC on file" : `${counts.activeNfcTokens} NFC on file`,
    );
  }
  if (parts.length > 0) {
    return parts.join(" · ");
  }
  // Active material whose token_type is neither QR nor NFC still counts as material on file.
  return counts.activeCheckpointMaterial === 1
    ? "1 tag on file"
    : `${counts.activeCheckpointMaterial} tags on file`;
}

export const ACTIVE_MATERIAL_INVENTORY_NOTE =
  "Raw secrets are shown once at mint. Inventory below shows what is on file — use Replace to issue new tags.";

export const CHECKPOINT_MATERIAL_MEDIA_NOTE =
  "One secret per mint: print/export the QR label or write the same value with an external NFC writer. Scan the QR in NFC Tools to import.";
